/**
 * Which cell goes where on one card of the entry list.
 *
 * The card is not arranged by anybody. Every zone is answered by something the
 * record already holds: a binding says which field is the name and which is the
 * number, `tier` says which fields go first, and `ord` is the order the shop
 * dragged its own form into. So one rule draws a sale, a due, a payout and a
 * stock movement, and a template nobody has written yet.
 *
 * The plan also says which ZONE every cell belongs to, so the component decides
 * nothing about placement: `zoneOf` reads the zone off the value type alone.
 *
 * Pure, and tested without a device. The components under
 * `components/entry-row` draw what this answers and decide nothing.
 *
 * Source: docs/design/entry-row, artboard "The rule", and
 * docs/design/entry-card-dense, direction D.
 */
import type {
  EntryListItem,
  EntryValue,
  EntryValues,
  RecordSchemaField,
  RecordSchemaResponse,
} from '@alluzo/shared';

import {
  chosenWordsOf,
  dayOf,
  fieldFor,
  formatClock,
  formatDayShort,
  measureOf,
  signOf,
  unitFieldIdOf,
} from './entry-format';
import { t, tOr } from './i18n';

// The words a multi select draws live beside every other value's words.
export { chosenWordsOf };

/** One field, and the value this entry holds for it. */
export type RowCell = {
  readonly field: RecordSchemaField;
  readonly value: EntryValue;
};

export type RowMeasure = {
  readonly field: RecordSchemaField;
  /** Unsigned, the way it is stored. `sign` says which way the row points. */
  readonly value: number;
  readonly sign: 1 | -1;
};

export type RowZone = 'fact' | 'computed' | 'mark' | 'note';

/** Where the ledger draws one field. Read off the value type alone. */
export const zoneOf = (field: RecordSchemaField): RowZone => {
  switch (field.type) {
    case 'formula':
    case 'rollup':
    case 'lookup':
      return 'computed';
    case 'select':
    case 'multi_select':
    case 'attachment':
      return 'mark';
    case 'long_text':
      return 'note';
    default:
      return 'fact';
  }
};

export type RowPlan = {
  /** The headline. Null when the record's title binding has nothing to say. */
  readonly title: RowCell | null;
  /** Drawn when `title` is null, so a card is never blank: "Sale · 6:40 pm". */
  readonly titleFallback: string;
  /** Every cell the card draws, in rule order. At most MAX_CARD_CELLS. */
  readonly cells: readonly RowCell[];
  /** `cells`, split by zone. Each keeps rule order. */
  readonly facts: readonly RowCell[];
  readonly computed: readonly RowCell[];
  readonly marks: readonly RowCell[];
  readonly notes: readonly RowCell[];
  /** How many filled fields the card did not draw. One count, never a row of glyphs. */
  readonly hidden: number;
  readonly measure: RowMeasure | null;
  /** Set only where the time axis carries a clock. A day is the group heading. */
  readonly time: RowCell | null;
};

/** Nine, and the reason is the height of a phone. With the headline and the
 *  amount that is eleven facts, about a third of a screen for the busiest card
 *  a shop is likely to make. Past nine a card is a form, and the form is one
 *  tap away. Two was the cap when the row could not wrap. */
export const MAX_CARD_CELLS = 9;

const NUMERIC = new Set(['money', 'number', 'quantity', 'formula', 'rollup']);

/**
 * Every row id a page of entries points at: each reference, and each row a
 * multi select over a list holds. The list page names them all in one read.
 */
export const referenceIdsOf = (
  schema: RecordSchemaResponse,
  rows: readonly EntryListItem[],
): Set<string> => {
  const ids = new Set<string>();
  const pointing = schema.fields.filter(
    (field) => field.type === 'reference' || (field.type === 'multi_select' && field.targetRecordId !== null),
  );
  for (const row of rows) {
    for (const field of pointing) {
      const value = row.values[field.id];
      for (const id of Array.isArray(value) ? value : [value]) {
        if (typeof id === 'string' && id !== '') ids.add(id);
      }
    }
  }
  return ids;
};

/**
 * Whether this row has something to draw for this field.
 *
 * Three answers are not values. A reference the page cannot name is a uuid, and
 * a uuid is not a word. An option the record no longer carries is a dangling
 * id. A number that will not parse is not a number. Everything else the entry
 * holds is a fact, including `false` and `0`.
 */
export const isFilled = (
  field: RecordSchemaField,
  value: EntryValue,
  references: Record<string, string>,
): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;

  if (field.type === 'reference') {
    return typeof value === 'string' && (references[value] ?? '') !== '';
  }
  // A run of lines is a count, and a row with none says nothing about it.
  if (field.type === 'lines') return typeof value === 'number' && value > 0;
  if (field.type === 'multi_select' && field.targetRecordId !== null) {
    // Row ids, which are words only once the list has named them.
    return chosenWordsOf(field, value, references).length > 0;
  }
  if (field.type === 'select') {
    return typeof value === 'string' && (field.options ?? []).some((option) => option.id === value);
  }
  if (NUMERIC.has(field.type)) {
    // pg hands numeric back as a string, so a row may carry either.
    return Number.isFinite(typeof value === 'number' ? value : Number(value));
  }
  return true;
};

/**
 * The record's own word for one row, at the start of a line.
 *
 * The catalogue holds nouns as a shop speaks them, in the middle of a sentence.
 * A line starts with a capital, and only English needs this one to be added.
 *
 * `tOr`: the key is the record's data, and a composed record may carry a noun
 * the catalogue does not hold. The plain noun stands in.
 */
export const entryNoun = (schema: RecordSchemaResponse): string => {
  const noun = tOr(schema.entryNounKey, t('noun.entry', { count: 1 }), { count: 1 });
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
};

/** The entry names itself when its record's title binding cannot. */
export const fallbackTitleOf = (schema: RecordSchemaResponse, entry: EntryListItem): string => {
  const named = entryNoun(schema);
  const clock = formatClock(entry.createdAt);
  return clock === '' ? named : `${named} · ${clock}`;
};

/**
 * What the header says over one entry: "Sale · 25 Aug".
 *
 * The noun is the record's, and the day is the one the record files this entry
 * under, which is its time axis and not the day it was typed.
 */
export const entryHeading = (schema: RecordSchemaResponse, entry: EntryListItem): string => {
  const day = dayOf(schema, entry);
  const drawn = day === null ? '' : formatDayShort(day);
  return drawn === '' ? entryNoun(schema) : `${entryNoun(schema)} · ${drawn}`;
};

const fieldById = (schema: RecordSchemaResponse, id: string | null): RecordSchemaField | null =>
  id === null ? null : (schema.fields.find((field) => field.id === id) ?? null);

/**
 * The row's values with each `lines` field answered by how many lines it
 * holds, where the row carries its lines (docs/plans/entry-lines-and-layout.md,
 * Decision 4: the parent stores nothing for a `lines` field). The card's
 * `line_count` cell then says "Rooms · 3 rooms". A row that carries no lines —
 * a mirror row on the phone — answers nothing for the field, and the card
 * says the count through a worked-out `agg count` field instead, which is how
 * Decision 19 counts guests on a booking.
 */
export const withLineCounts = (schema: RecordSchemaResponse, entry: EntryListItem): EntryValues => {
  const lines = entry.lines;
  if (lines === undefined) return entry.values;
  let out: Record<string, unknown> | null = null;
  for (const field of schema.fields) {
    if (field.type !== 'lines') continue;
    const run = lines[field.id];
    if (run === undefined) continue;
    out ??= { ...entry.values };
    out[field.id] = run.length;
  }
  return (out ?? entry.values) as EntryValues;
};

const cellOf = (field: RecordSchemaField, values: EntryValues): RowCell => ({
  field,
  value: values[field.id] ?? null,
});

/**
 * The whole card, in one pass over the field set.
 *
 * Order matters here. Every zone that claims a field marks it spoken, so the
 * cells cannot repeat the headline and the count cannot repeat either.
 */
export const planRow = (
  schema: RecordSchemaResponse,
  entry: EntryListItem,
  references: Record<string, string>,
): RowPlan => {
  const values = withLineCounts(schema, entry);
  const spoken = new Set<string>();
  const filled = (field: RecordSchemaField): boolean =>
    isFilled(field, values[field.id] ?? null, references);

  const measureField = fieldById(schema, fieldFor(schema, 'measure'));
  const axisField = fieldById(schema, fieldFor(schema, 'time_axis'));
  const boardField = fieldById(schema, fieldFor(schema, 'board_axis'));
  const titleField = fieldById(schema, fieldFor(schema, 'title'));

  if (measureField !== null) spoken.add(measureField.id);
  if (axisField !== null) spoken.add(axisField.id);
  // A unit is drawn by the quantity it belongs to, so it is never a cell of its
  // own and never part of the count. "4 box" is one fact, not two.
  for (const field of schema.fields) {
    const unitFieldId = unitFieldIdOf(field);
    if (unitFieldId !== null) spoken.add(unitFieldId);
  }

  const title = titleField !== null && filled(titleField) ? cellOf(titleField, values) : null;
  if (title !== null) spoken.add(title.field.id);

  // The board axis is the record's own status. It goes first whatever tier it
  // wears, because a due that is settled is not the same row as one that is
  // open.
  const first = schema.fields
    .filter((field) => !spoken.has(field.id))
    .filter((field) => field.tier === 'primary' || field.id === boardField?.id)
    .filter(filled)
    .sort((a, b) => a.ord - b.ord);
  for (const field of first) spoken.add(field.id);

  // Then everything else the entry holds, in form order. The card has the room
  // for it now, and a field a shop added is a field a shop wants read.
  const rest = schema.fields
    .filter((field) => !spoken.has(field.id))
    .filter(filled)
    .sort((a, b) => a.ord - b.ord);

  const ordered = [...first, ...rest].map((field) => cellOf(field, values));
  const cells = ordered.slice(0, MAX_CARD_CELLS);
  const hidden = ordered.length - cells.length;
  const inZone = (zone: RowZone): readonly RowCell[] =>
    cells.filter((cell) => zoneOf(cell.field) === zone);

  const raw = measureOf(schema, values);
  const measure =
    measureField === null || raw === null
      ? null
      : { field: measureField, value: raw, sign: signOf(schema, values) ?? 1 };

  const time =
    axisField !== null && axisField.type === 'datetime' && filled(axisField)
      ? cellOf(axisField, values)
      : null;

  return {
    title,
    titleFallback: fallbackTitleOf(schema, entry),
    cells,
    facts: inZone('fact'),
    computed: inZone('computed'),
    marks: inZone('mark'),
    notes: inZone('note'),
    hidden,
    measure,
    time,
  };
};

/** The card's one overflow marker: "+3 more". Empty when the card drew everything. */
export const moreLabel = (hidden: number): string =>
  hidden <= 0 ? '' : `+${hidden} ${t('entry.more')}`;

/** "1 attachment", "2 attachments". A bare 2 next to a quantity reads as a quantity. */
export const attachmentsLabel = (count: number): string =>
  t('entry.attachments', { count });
